<template>
  <div v-if="visible" class="modal-background">
    <div class="modal-placeholder">
      <div class="modal-bg">
        <div class="header has-text-centered">
          <h2 class="has-text-weight-bold is-size-3">
            {{ $t("evaluation.projectTitle") }}
          </h2>
        </div>
        <div class="column">
          <p class="rating-helper">
            {{ $t("evaluation.helper.intro") }}
          </p>
          <div
            class="comment-evaluation"
            :class="{ 'needs-comment': evaluation.quality < 3 }"
          >
            <star-rating
              :label="$t('evaluation.quality.label')"
              :subLabel="[
                $t('evaluation.quality.specs'),
                $t('evaluation.quality.materials'),
                $t('evaluation.quality.controls'),
                $t('evaluation.quality.compliance'),
                $t('evaluation.quality.installation'),
                $t('evaluation.quality.packaging'),
                $t('evaluation.quality.execution'),
                $t('evaluation.quality.incidents'),
                $t('evaluation.quality.value'),
              ]"
              :number-of-stars="5"
              @rating-selected="handleRatingQuality"
            ></star-rating>
            <textarea
              class="textarea"
              v-model="evaluation.qualityComment"
              :required="evaluation.quality < 3"
              :placeholder="
                evaluation.quality < 3
                  ? $t('evaluation.helper.commentRequired')
                  : $t('evaluation.helper.commentOptional')
              "
              rows="1"
            ></textarea>
            <p
              v-if="
                formSubmitted &&
                evaluation.quality < 3 &&
                !evaluation.qualityComment
              "
              class="error-message"
            >
              {{ $t("evaluation.helper.errorMessage") }}
            </p>
          </div>

          <div
            class="comment-evaluation"
            :class="{ 'needs-comment': evaluation.reactivity < 3 }"
          >
            <star-rating
              :label="$t('evaluation.deadlines.label')"
              :subLabel="[
                $t('evaluation.deadlines.offers'),
                $t('evaluation.deadlines.intervention'),
                $t('evaluation.deadlines.reactivity'),
                $t('evaluation.deadlines.availability'),
                $t('evaluation.deadlines.contractual'),
                $t('evaluation.deadlines.commitments'),
              ]"
              :number-of-stars="5"
              @rating-selected="handleRatingReactivity"
            ></star-rating>
            <textarea
              class="textarea"
              v-model="evaluation.reactivityComment"
              :required="evaluation.reactivity < 3"
              :placeholder="
                evaluation.reactivity < 3
                  ? $t('evaluation.helper.commentRequired')
                  : $t('evaluation.helper.commentOptional')
              "
              rows="1"
            ></textarea>
            <p
              v-if="
                formSubmitted &&
                evaluation.reactivity < 3 &&
                !evaluation.reactivityComment
              "
              class="error-message"
            >
              {{ $t("evaluation.helper.errorMessage") }}
            </p>
          </div>

          <div
            class="comment-evaluation"
            :class="{ 'needs-comment': evaluation.technic < 3 }"
          >
            <star-rating
              :label="$t('evaluation.technical.label')"
              :subLabel="[
                $t('evaluation.technical.studies'),
                $t('evaluation.technical.plans'),
                $t('evaluation.technical.aptitude'),
                $t('evaluation.technical.proposition'),
                $t('evaluation.technical.innovation'),
                $t('evaluation.technical.qualification'),
                $t('evaluation.technical.assistance'),
                $t('evaluation.technical.organization'),
                $t('evaluation.technical.responses'),
              ]"
              :number-of-stars="5"
              @rating-selected="handleRatingTechnic"
            ></star-rating>
            <textarea
              class="textarea"
              v-model="evaluation.technicComment"
              :required="evaluation.technic < 3"
              :placeholder="
                evaluation.technic < 3
                  ? $t('evaluation.helper.commentRequired')
                  : $t('evaluation.helper.commentOptional')
              "
              rows="1"
            ></textarea>
            <p
              v-if="
                formSubmitted &&
                evaluation.technic < 3 &&
                !evaluation.technicComment
              "
              class="error-message"
            >
              {{ $t("evaluation.helper.errorMessage") }}
            </p>
          </div>

          <div class="skip-container">
            <input
              type="checkbox"
              v-model="skipEvaluation"
              id="skipEvaluation"
            />
            <label for="skipEvaluation">
              {{ $t("evaluation.helper.skipLabel") }}
            </label>
          </div>
        </div>
        <div class="buttons-container">
          <button class="button is-danger" @click="cancel">
            {{ $t("evaluation.buttons.cancel") }}
          </button>
          <button
            class="button is-success"
            @click="submitForm"
            :disabled="skipEvaluation"
          >
            {{ $t("evaluation.buttons.submit") }}
          </button>
          <button class="button" @click="skip" :disabled="!skipEvaluation">
            {{ $t("evaluation.buttons.skip") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      visible: false,
      formSubmitted: false,
      evaluation: {
        technic: 0,
        technicComment: "",
        reactivity: 0,
        reactivityComment: "",
        quality: 0,
        qualityComment: "",
      },
      skipEvaluation: false,
    };
  },
  methods: {
    getRatingDescription(rating) {
      return this.$t(`evaluation.ratings.${rating}`);
    },
    open() {
      this.visible = true;
    },
    close() {
      this.visible = false;
      this.formSubmitted = false;
    },
    handleRatingTechnic(rating) {
      this.evaluation.technic = rating;
    },
    handleRatingReactivity(rating) {
      this.evaluation.reactivity = rating;
    },
    handleRatingQuality(rating) {
      this.evaluation.quality = rating;
    },
    skip() {
      this.$emit("skip");
      this.close();
    },
    submitForm() {
      this.formSubmitted = true;
      if (
        (this.evaluation.technic < 3 && !this.evaluation.technicComment) ||
        (this.evaluation.reactivity < 3 &&
          !this.evaluation.reactivityComment) ||
        (this.evaluation.quality < 3 && !this.evaluation.qualityComment)
      ) {
        return;
      }

      this.$emit("submit", this.evaluation);
      this.close();
    },
    cancel() {
      this.$emit("cancel");
      this.close();
    },
  },
};
</script>

<style>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 5px;
}
.comment-evaluation {
  margin-bottom: 35px;
  padding: 20px;
  border-radius: 8px;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.comment-evaluation:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.textarea {
  margin-top: 15px;
  border-radius: 6px;
  border: 1px solid #dee2e6;
  padding: 12px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease;
  resize: vertical;
  min-height: 60px;
}

.textarea:focus {
  border-color: #4a90e2;
  box-shadow: 0 0 0 2px rgba(74, 144, 226, 0.1);
}

.error-message {
  color: #dc3545;
  font-size: 0.85rem;
  margin-top: 8px;
  padding: 8px 12px;
  background-color: #fff5f5;
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}

.modal-placeholder {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  height: 100%;
}

.modal-bg {
  position: fixed;
  left: 40%;
  right: 0;
  top: 0;
  height: 100%;
  background-color: #fff;
  padding: 30px 40px;
  overflow: auto;
}
.modal-background {
  position: fixed !important;
  top: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  z-index: 35;
}

.header {
  margin-bottom: 15px;
  border-bottom: 1px solid #eee;
}

.skip-container {
  display: flex;
  align-items: center;
  margin-top: 30px;
  padding: 15px;
  background-color: #fff8dc;
  border-radius: 6px;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-right: 12px;
  }

  p {
    margin-left: 10px;
    font-size: 0.9rem;
    color: #666;
  }
}

.button {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;

  &.is-success {
    background-color: #28a745;
    &:hover {
      background-color: #218838;
    }
  }

  &.is-danger {
    background-color: #dc3545;
    &:hover {
      background-color: #c82333;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.rating-helper {
  font-size: 0.85rem;
  color: #666;
  margin: 8px 0;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #4a90e2;
  margin-bottom: 30px;
}

.needs-comment {
  border-left: 3px solid #dc3545;
}

.buttons-container {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 15px;
}

.comment-evaluation {
  position: relative;
  border-left: 3px solid transparent;
}
</style>
